import { PaginationNav } from "@/components/Pagination/PaginationNav";
import { NoCourses } from "@/partials/course/NoCourses";
import { hasCoursesQueryOptions } from "@/queries/courses/hasCoursesQuery";
import { publishedCoursesCountQueryOptions } from "@/queries/courses/publishedCoursesCountQuery";
import { inReviewCoursesCountQueryOptions } from "@/queries/courses/inReviewCoursesCountQuery";
import { allCoursesCountQueryOptions } from "@/queries/courses/allCoursesCountQuery";
import { getMaxCourseLimit } from "@/utils/courseLimit";
import { shouldShowUpgradePopup } from "@/utils/planCheck";
import { useQuery } from "@tanstack/react-query";
import { allCoursesQueryOptions, paymentInfoQueryOptions } from "@vixi/api";
import { LangKey, useTranslation } from "@vixi/localization";
import { RouteOptionsById, router, RouterIds, useNavigate } from "@vixi/router";
import { COURSE_STATUS, COURSE_TYPE, LabelValuePair, ShallowCourse } from "@vixi/types";
import {
  Button,
  CourseCard,
  Divider,
  FilterTabs,
  PageMainContent,
  StatCard,
  useGoBack,
  useUiStore,
  Img,
} from "@vixi/ui";
import ChevronRightCircle from "@assets/icons/chevron-right-circle.svg?react";
import { genArray } from "@vixi/utils";
import { useEffect, useMemo, useState } from "react";

// Constants
const routeId = "/_app/courses/" satisfies RouterIds;
const Route = router.routesById[routeId];

const courses_per_page = 3;
const grid_item_width = 300;
const grid_item_gap = 37;
const course_grid_width =
  grid_item_width * courses_per_page + grid_item_gap * (courses_per_page - 1);

export const CoursesIndexPageOptions: RouteOptionsById<typeof routeId> = {
  component: RouteComponent,
  loader: async ({
    context: { queryClient },
    deps: {
      search: { pageSize, ...search },
    },
  }) => {
    // Pre-fetch courses data
    queryClient.ensureQueryData(allCoursesQueryOptions({ ...search, pageSize: courses_per_page }));
    // Pre-fetch published courses count to avoid NaN
    queryClient.ensureQueryData(publishedCoursesCountQueryOptions());
    // Pre-fetch in-review courses count
    queryClient.ensureQueryData(inReviewCoursesCountQueryOptions());
    // Pre-fetch all courses count (for popup check)
    queryClient.ensureQueryData(allCoursesCountQueryOptions());
    // Pre-fetch payment info for max courses limit
    queryClient.ensureQueryData(paymentInfoQueryOptions());
  },
};

const courseStates: LabelValuePair<LangKey, COURSE_STATUS | undefined>[] = [
  {
    label: "Common.All" as const,
    value: undefined,
  },
  {
    label: "CourseState.Published" as const,
    value: COURSE_STATUS.PUBLISHED,
  },
  {
    label: "CourseState.InReview" as const,
    value: COURSE_STATUS.WAITING_PUBLISH,
  },
  {
    label: "CourseState.Draft" as const,
    value: COURSE_STATUS.DRAFT,
  },
];

function RouteComponent() {
  useGoBack(null);
  const { t } = useTranslation();
  const { showConfirmDialog } = useUiStore();

  const fallback_courseItems: ShallowCourse[] = useMemo(
    () =>
      genArray(
        (i) =>
          ({
            id: i - 3,
            categoryId: 1,
            courseType: COURSE_TYPE.EBOOK,
            title: "",
            eBookAbout: "eBookAbout",
            targetAudience: "targetAudience",
            conversationTone: "conversationTone",
            description: "description",
            aboutTheAuthor: "aboutTheAuthor",
            courseStatus: COURSE_STATUS.WAITING_PUBLISH,
          }) satisfies ShallowCourse,
        3,
      ),
    [],
  );

  const { pageSize, status: selectedStatus, ...search } = Route.useSearch();
  const {
    data: courseData,
    isFetching,
    isSuccess,
  } = useQuery(
    allCoursesQueryOptions({ ...search, status: selectedStatus, pageSize: courses_per_page }),
  );
  const { data: hasCourses, isPending: hasCoursesPending } = useQuery(hasCoursesQueryOptions());

  // Dynamic Stats
  const { data: publishedCoursesCount, isSuccess: isPublishedCoursesSuccess } = useQuery(
    publishedCoursesCountQueryOptions(),
  );
  const { data: inReviewCoursesCount } = useQuery(inReviewCoursesCountQueryOptions());
  const { data: allCoursesCount } = useQuery(allCoursesCountQueryOptions());
  const { data: paymentInfo, isSuccess: isPaymentInfoSuccess } = useQuery(paymentInfoQueryOptions());

  // Debug: Log data when loaded
  useEffect(() => {
    if (isPaymentInfoSuccess) {
      console.log("✅ [courses/index] PaymentInfo loaded:", paymentInfo);
    }
    if (isPublishedCoursesSuccess) {
      console.log("✅ [courses/index] PublishedCoursesCount loaded:", publishedCoursesCount);
    }
    if (allCoursesCount !== undefined) {
      console.log("✅ [courses/index] AllCoursesCount loaded:", allCoursesCount);
    }
  }, [isPaymentInfoSuccess, isPublishedCoursesSuccess, paymentInfo, publishedCoursesCount, allCoursesCount]);

  // Ensure we always have valid numbers, never NaN or undefined
  // Use 0 as fallback during initial load to prevent NaN display
  const publishedCourses: number = Number.isFinite(publishedCoursesCount) && typeof publishedCoursesCount === "number"
    ? publishedCoursesCount
    : 0;
  const inReviewCourses: number = Number.isFinite(inReviewCoursesCount) && typeof inReviewCoursesCount === "number"
    ? inReviewCoursesCount
    : 0;
  const maxCourses: number = getMaxCourseLimit(paymentInfo, 3);
  const remainingCourses: number = Number.isFinite(maxCourses - publishedCourses)
    ? Math.max(0, maxCourses - publishedCourses)
    : 0;

  // Pagination
  const navigate = Route.useNavigate();
  
  // Handle IncreaseLimit button click
  const handleIncreaseLimitClick = () => {
    console.log("🔍 [courses/index] IncreaseLimit clicked:", {
      paymentInfo,
      publishedCourses,
      publishedCoursesCount,
      allCoursesCount,
      typeOfAllCoursesCount: typeof allCoursesCount,
      isAllCoursesCountOne: allCoursesCount === 1,
    });
    
    // Use allCoursesCount (all courses regardless of status), not just published
    const shouldShow = shouldShowUpgradePopup(paymentInfo, allCoursesCount);
    console.log("🔍 [courses/index] shouldShowUpgradePopup result:", shouldShow);
    
    if (shouldShow) {
      showConfirmDialog({
        title: null,
        description: (
          <div className="flex flex-col items-center justify-center w-full h-full relative px-6 py-6">
            {/* Logo and Premium Text */}
            <div className="flex items-center mb-4">
              <Img src="/images/popuplogo.png" alt="Vixi Premium" className="w-[183px] h-[75px]" />
              <span className="text-[#B57CFF] text-[20px] font-bold">Premium</span>
            </div>

            {/* Premium Popup Image */}
            <div className="flex items-center justify-center w-full mb-4">
              <Img
                src="/images/planpopup.png"
                alt="Vixi Premium"
                className="w-full max-h-[200px]"
                contain
              />
            </div>

            {/* Text */}
            <h1 className="text-[35px] font-bold text-center text-[#334D6E] mb-4">
              Generate and publish more courses !
            </h1>

            {/* Try Vixi Premium Button */}
            <Button
              variant="contained"
              color="premium"
              size="large"
              className="px-8 py-3 rounded-full bg-gradient-to-r from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 text-white font-medium"
              startIcon={<ChevronRightCircle className="w-5 h-5" />}
              onClick={() => {
                useUiStore.getState().hideConfirmDialog();
                navigate({ to: "/premium/plans" });
              }}
            >
              {"Try Vixi Premium"}
            </Button>
          </div>
        ),
        dialogActions: null,
      });
    } else {
      // Navigate to premium plans if not on free plan or hasn't reached limit
      navigate({ to: "/premium/plans" });
    }
  };
  const goToPage = (pageNumber: number) =>
    navigate({ to: "/courses", search: (prev) => ({ ...prev, pageNumber }) });
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (courseData?.totalPages) setTotalPages(courseData?.totalPages);
  }, [courseData, courseData?.totalPages]);

  const isLoading = isFetching && !courseData?.items;
  const noCourses = isSuccess && !courseData?.totalCount;

  const courseDataItems = courseData?.items ?? fallback_courseItems;

  return (
    <PageMainContent psY="medium" scrollable>
      <FilterTabs
        className="mx-auto"
        tabs={courseStates.map((x) => ({ ...x, label: t(x.label) }))}
        value={selectedStatus}
        onChange={(v) => navigate({ search: (prev) => ({ ...prev, status: v, pageNumber: 1 }) })}
      />

      {/* Stats */}
      <div
        className="mx-auto mt-10 h-42.5 min-h-fit max-w-full"
        style={{ width: course_grid_width }}
      >
        <div className="flex min-h-fit flex-col gap-9.25 md:flex-row">
          <StatCard
            pretitle={"Courses.CurrentlyHosting"}
            value={publishedCourses}
            title={"CourseState.PublishedCourses"}
          />
          <StatCard
            pretitle={"Courses.CanStillHost"}
            value={remainingCourses}
            title={remainingCourses != 1 ? "Courses.Courses" : "Courses.CourseOnly"}
            prompt={
              <Button
                variant="outlined"
                color="premium"
                size="pill"
                className="fs-16 self-center"
                onClick={handleIncreaseLimitClick}
              >
                {t("Action.IncreaseLimit")}
              </Button>
            }
          />
          <StatCard
            pretitle={"Courses.CurrentlyHave"}
            value={inReviewCourses}
            title={inReviewCourses !== 1 ? "CourseState.CoursesUnderReview" : "CourseState.CourseUnderReview"}
          />
        </div>

        <h3 className="mt-5 font-medium">
          {selectedStatus === COURSE_STATUS.PUBLISHED && t("Courses.PublishedCoursesMessage")}
          {selectedStatus === COURSE_STATUS.DRAFT && t("Courses.DraftCoursesMessage")}
          {selectedStatus === COURSE_STATUS.WAITING_PUBLISH && t("Courses.InReviewCoursesMessage")}
        </h3>

        <Divider className="mt-7.5" />
      </div>

      {/* Courses */}
      {!hasCoursesPending && !hasCourses ? (
        <>
          <NoCourses />
        </>
      ) : courseDataItems?.length === 0 ? (
        <>
          <h4 className="">{t("Common.NoResultsFound")}</h4>
        </>
      ) : (
        <div className="mt-7.5 flex min-h-fit flex-col">
          <div
            className="mx-auto flex max-w-full flex-wrap justify-center gap-9.25"
            style={{ width: course_grid_width }}
          >
            {courseDataItems?.map((course) => (
              <div key={course.id} className="w-75">
                <CourseCard
                  loading={isLoading}
                  course={course}
                  image={course.courseCharacters?.[0]?.url}
                />
              </div>
            ))}
          </div>

          <div className="mx-auto mt-2.5">
            <PaginationNav
              visiblePages={3}
              totalPages={totalPages}
              activePage={courseData?.pageNumber ?? search.pageNumber ?? 1}
              onGoToPage={goToPage}
            />
          </div>
        </div>
      )}
    </PageMainContent>
  );
}
